import { Tabs } from 'antd';
import ExcelTable from 'components/ui/excel-table';
import { TOTAL_ROW_KEY } from 'components/ui/excel-table/constants';
import {
  ExcelTableColDef,
  ECellValueType,
  ECellValueDisplayMode,
} from 'components/ui/excel-table/types';
import { DEFAULT_LAYER_RESULT } from 'constants/layer-results';
import { EQueryKeys } from 'constants/query-keys';
import useSocketIO, { ESocketEvents } from 'hooks/useWebSocket';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { layerResultApi, priceRatingGatewayUrl } from 'services/apis';
import { IPriceRatingUpdatedMessage } from 'services/apis/types';

type LayerResultTableProps = {
  currency: string;
  submissionId: string;
};

type LayerResultDisplayedData = {
  layerNumber: number;
  layerName: string;
  rateAdequacy: string;
  exposureFull: string;
  pinnacleExposure: string;
  premium: string;
  premiumNet: string;
  lossCost: string;
  rateOnLine: string;
  rateOnExposure: string;
  lineSize: string;
  expectedULR: string;
  targetedMLR: string;
  ULRAdequacy: string;
  shareOfPremiumNet: string;
  shareOfExpectedLossCost: string;
  expectedProfit: string;
  returnOnCapital: string;
};

const layerResultCategoryTabs = [
  {
    key: 'totalCatNonCat',
    label: 'Total CAT & Non-CAT',
    resultTypes: [],
    includeAll: true,
  },
  {
    key: 'totalAllCat',
    label: 'Total All CAT',
    resultTypes: ['earthquake', 'wind', 'flood'],
  },
  { key: 'fire', label: 'Fire', resultTypes: ['fire'] },
  { key: 'earthquake', label: 'Earthquake', resultTypes: ['earthquake'] },
  { key: 'wind', label: 'Wind', resultTypes: ['wind'] },
  { key: 'flood', label: 'Flood', resultTypes: ['flood'] },
];

const LayerResultTable = ({
  currency,
  submissionId,
}: LayerResultTableProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<string>('totalCatNonCat');

  const { data } = useQuery(
    [EQueryKeys.GET_LAYER_RESULTS, submissionId, activeTab, currency],
    () => {
      const activeTabCategory = layerResultCategoryTabs.find(
        (tab) => tab.key === activeTab
      );
      return layerResultApi.getLayerResults({
        submissionId,
        layerResultsFilter: {
          types: activeTabCategory?.resultTypes,
          includeAll: activeTabCategory?.includeAll ?? false,
          currency,
        },
      });
    },
    {
      enabled: !!submissionId,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const { sendMessage } = useSocketIO<IPriceRatingUpdatedMessage>({
    url: priceRatingGatewayUrl,
    requireAuth: true,
    onConnect: () => {
      sendMessage(ESocketEvents.SUBSCRIBE, {
        submissionId,
      });
    },
    registerEvents: [
      {
        event: ESocketEvents.PRICE_RATING_UPDATED,
        onMessage: (message) => {
          if (message.submissionId === submissionId) {
            queryClient.invalidateQueries([
              EQueryKeys.GET_LAYER_RESULTS,
              submissionId,
              activeTab,
              currency,
            ]);
          }
        },
      },
    ],
  });

  // integrate API here
  const rowData: LayerResultDisplayedData[] = (data?.layers ?? []).map(
    (layerResultData) => ({
      ...layerResultData,
      ULRAdequacy: layerResultData.ulrAdequacy,
      expectedULR: layerResultData.expectedULR,
      layerName: `Layer ${layerResultData.layerNumber}`,
    })
  );

  // integrate API here
  const rowSummaryData = useMemo<LayerResultDisplayedData>(() => {
    return {
      ...DEFAULT_LAYER_RESULT,
      ...data?.summary,
      ULRAdequacy: data?.summary.ulrAdequacy ?? '0',
      id: TOTAL_ROW_KEY,
      layerNumber: rowData.length + 1,
      layerName: 'Total',
    };
  }, [rowData.length, data]);

  const columnDefs = useMemo<
    ExcelTableColDef<LayerResultDisplayedData, string | number>[]
  >(() => {
    const columns: ExcelTableColDef<
      LayerResultDisplayedData,
      string | number
    >[] = [
      {
        field: 'layerName',
        minWidth: 125,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'rateAdequacy',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'exposureFull',
        resizable: true,
        minWidth: 160,
        flex: 1,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'premiumNet',
        resizable: true,
        minWidth: 145,
        flex: 1,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'lossCost',
        minWidth: 145,
        flex: 1,
        resizable: true,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'lineSize',
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'rateOnLine',
        minWidth: 150,
        flex: 1,
        editable: false,
        resizable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'rateOnExposure',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'expectedULR',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'targetedMLR',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'ULRAdequacy',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
      {
        field: 'shareOfPremiumNet',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'shareOfExpectedLossCost',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'expectedProfit',
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        field: 'returnOnCapital',
        minWidth: 170,
        flex: 1,
        resizable: true,
        editable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
      },
    ];

    return columns.map((c) => ({
      ...c,
      headerName: t(`layerResults.tableHeaders.${c.field}`),
    }));
  }, [t]);

  return (
    <div className="bg-white py-3 rounded-lg">
      <Tabs
        items={layerResultCategoryTabs}
        defaultActiveKey="totalCatNonCat"
        onChange={(activeTab) => setActiveTab(activeTab)}
        tabBarStyle={{ margin: 0 }}
        className="custom-tabs inline-block bg-[#e3f2fb] mx-[16px] rounded-lg my-0 py-0"
      />
      <ExcelTable
        rowKeyColName="id"
        columnDefs={columnDefs}
        data={rowData}
        includeAddRow={false}
        includeDeleteCol={false}
        includeIdRow={false}
        containerClassName="rounded-lg"
        tableContainerClassName="p-3"
        detailRowAutoHeight={true}
        pagination={false}
        summaryRowData={rowSummaryData}
        domLayout="autoHeight"
      />
    </div>
  );
};

export default LayerResultTable;
